/**
 * Parsing + formatting for the GMC source shipping attributes (the merchant
 * feed's own shipping / logistics / physical values). Every surface — the
 * product-detail section, version history and the enrichment rows — goes
 * through here so the 17 attributes read the same everywhere.
 * See `docs/gmc-source-shipping-attributes.md`.
 */

import type { TFunction } from 'i18next';

import type {
  FreeShippingThreshold,
  Measurement,
  ShippingEntry,
  ShippingPrice,
  SourceShippingAttributes,
} from '~/types/source-shipping';

/** How a single attribute's value is shaped on the wire — drives parse + format. */
type SourceShippingKind =
  | 'shipping'
  | 'threshold'
  | 'measurement'
  | 'text'
  | 'days'
  | 'boolean'
  | 'count';

/** Opt-in query param for the workflow-service enrichment reads: without it the
 *  rows carry no `sourceShippingAttributes` (the response is byte-for-byte legacy). */
export const INCLUDE_SOURCE_SHIPPING_PARAM = { includeSourceShipping: 'true' } as const;

/** One attribute's descriptor: UI (camelCase) key, curation column name, value kind. */
export interface SourceShippingFieldDef {
  key: keyof SourceShippingAttributes;
  wireKey: string;
  kind: SourceShippingKind;
}

/** The 17 attributes in display order. `wireKey` is the curation snake_case column;
 *  `key` is both the UI key and the workflow-service camelCase key. */
export const SOURCE_SHIPPING_FIELDS: readonly SourceShippingFieldDef[] = [
  { key: 'shipping', wireKey: 'shipping', kind: 'shipping' },
  { key: 'freeShippingThreshold', wireKey: 'free_shipping_threshold', kind: 'threshold' },
  { key: 'shippingLabel', wireKey: 'shipping_label', kind: 'text' },
  { key: 'transitTimeLabel', wireKey: 'transit_time_label', kind: 'text' },
  { key: 'minHandlingTime', wireKey: 'min_handling_time', kind: 'days' },
  { key: 'maxHandlingTime', wireKey: 'max_handling_time', kind: 'days' },
  { key: 'shippingWeight', wireKey: 'shipping_weight', kind: 'measurement' },
  { key: 'shippingLength', wireKey: 'shipping_length', kind: 'measurement' },
  { key: 'shippingWidth', wireKey: 'shipping_width', kind: 'measurement' },
  { key: 'shippingHeight', wireKey: 'shipping_height', kind: 'measurement' },
  { key: 'productWeight', wireKey: 'product_weight', kind: 'measurement' },
  { key: 'productLength', wireKey: 'product_length', kind: 'measurement' },
  { key: 'productWidth', wireKey: 'product_width', kind: 'measurement' },
  { key: 'productHeight', wireKey: 'product_height', kind: 'measurement' },
  { key: 'isBundle', wireKey: 'is_bundle', kind: 'boolean' },
  { key: 'multipack', wireKey: 'multipack', kind: 'count' },
  { key: 'shortTitle', wireKey: 'short_title', kind: 'text' },
];

const FIELD_BY_WIRE_KEY = new Map(SOURCE_SHIPPING_FIELDS.map((f) => [f.wireKey, f]));

/** True for a curation column name that is one of the 17 source shipping attributes. */
export function isSourceShippingWireKey(key: string): boolean {
  return FIELD_BY_WIRE_KEY.has(key);
}

// ---------------------------------------------------------------------------
// Defensive parsing — the wire is typed, but these are brand-new columns and an
// unexpected shape must degrade to "not provided", never crash a render.
// ---------------------------------------------------------------------------

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function asFiniteNumber(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function asText(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed === '' ? null : trimmed;
}

/** `{ value, unit }` → Measurement, or null when neither half is usable. */
function asMeasurement(value: unknown): Measurement | null {
  if (!isRecord(value)) return null;
  const m = { value: asFiniteNumber(value.value), unit: asText(value.unit) };
  return m.value === null && m.unit === null ? null : m;
}

/** `{ value, currency }` → ShippingPrice, or null when neither half is usable. */
function asPrice(value: unknown): ShippingPrice | null {
  if (!isRecord(value)) return null;
  const p = { value: asFiniteNumber(value.value), currency: asText(value.currency) };
  return p.value === null && p.currency === null ? null : p;
}

/** Keeps the object entries of a `shipping` array, normalising nested `price`. */
function asShippingEntries(value: unknown): ShippingEntry[] | null {
  if (!Array.isArray(value)) return null;
  const entries = value.filter(isRecord).map((raw) => {
    const { price, ...rest } = raw;
    const parsedPrice = asPrice(price);
    return (parsedPrice ? { ...rest, price: parsedPrice } : rest) as ShippingEntry;
  });
  return entries.length > 0 ? entries : null;
}

/** Keeps the object entries of a `free_shipping_threshold` array. */
function asThresholds(value: unknown): FreeShippingThreshold[] | null {
  if (!Array.isArray(value)) return null;
  const entries = value.filter(isRecord).map((raw) => {
    const country = asText(raw.country);
    const threshold = asPrice(raw.price_threshold);
    return {
      ...(country ? { country } : {}),
      ...(threshold ? { price_threshold: threshold } : {}),
    };
  });
  return entries.length > 0 ? entries : null;
}

/** Parses one raw value by kind. Returns null for absent/unusable values. */
function parseByKind(kind: SourceShippingKind, value: unknown): unknown {
  switch (kind) {
    case 'shipping':
      return asShippingEntries(value);
    case 'threshold':
      return asThresholds(value);
    case 'measurement':
      return asMeasurement(value);
    case 'text':
      return asText(value);
    case 'days':
    case 'count':
      return asFiniteNumber(value);
    case 'boolean':
      return typeof value === 'boolean' ? value : null;
  }
}

/**
 * Builds `SourceShippingAttributes` from either wire shape: curation's top-level
 * snake_case columns (`casing: 'snake'`) or workflow's camelCase
 * `sourceShippingAttributes` object (`casing: 'camel'`). Returns `undefined` when
 * the feed states none of the 17 — callers hide the section rather than render 17 blanks.
 */
export function toSourceShippingAttributes(
  raw: unknown,
  casing: 'snake' | 'camel',
): SourceShippingAttributes | undefined {
  if (!isRecord(raw)) return undefined;
  const out: Record<string, unknown> = {};
  let anySet = false;
  for (const field of SOURCE_SHIPPING_FIELDS) {
    const parsed = parseByKind(field.kind, raw[casing === 'snake' ? field.wireKey : field.key]);
    out[field.key] = parsed;
    if (parsed !== null) anySet = true;
  }
  return anySet ? (out as unknown as SourceShippingAttributes) : undefined;
}

// ---------------------------------------------------------------------------
// Formatting
// ---------------------------------------------------------------------------

const decimalFormatter = new Intl.NumberFormat(undefined, { maximumFractionDigits: 3 });

/** "4.5 kg" — the unit verbatim as the feed gave it; either half may be missing. */
export function formatMeasurement(m: Measurement | null): string | undefined {
  if (!m) return undefined;
  const parts = [m.value !== null ? decimalFormatter.format(m.value) : null, m.unit];
  const text = parts.filter((p): p is string => p !== null).join(' ');
  return text === '' ? undefined : text;
}

/** "$12.00" via Intl; falls back to "12 XYZ" when the currency code isn't ISO-valid.
 *  A plain money formatter — callers decide what a 0 means in their context. */
export function formatShippingPrice(p: ShippingPrice | null | undefined): string | undefined {
  if (!p) return undefined;
  if (p.value === null) return p.currency ?? undefined;
  if (p.currency) {
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency: p.currency }).format(
        p.value,
      );
    } catch {
      return `${decimalFormatter.format(p.value)} ${p.currency}`;
    }
  }
  return decimalFormatter.format(p.value);
}

/** Business-day range: "1–3 business days", "2 business days", "from 1 …", "up to 3 …". */
export function formatDayRange(
  min: number | null | undefined,
  max: number | null | undefined,
  t: TFunction<'common'>,
): string | undefined {
  const lo = typeof min === 'number' ? min : null;
  const hi = typeof max === 'number' ? max : null;
  if (lo !== null && hi !== null) {
    return lo === hi
      ? t('sourceShipping.days', { count: lo })
      : t('sourceShipping.dayRange', { min: lo, max: hi });
  }
  if (lo !== null) return t('sourceShipping.fromDays', { count: lo });
  if (hi !== null) return t('sourceShipping.upToDays', { count: hi });
  return undefined;
}

/** A shipping rule's cost: a price of exactly 0 is free shipping (contract rule),
 *  not a missing price, so it reads "Free" rather than "$0.00". */
function formatShippingCost(
  price: ShippingPrice | null | undefined,
  t: TFunction<'common'>,
): string | undefined {
  if (price?.value === 0) return t('sourceShipping.free');
  return formatShippingPrice(price);
}

/** "1600" / "930" / "16:00" → "16:00"; anything else is returned as given. */
function formatCutoffTime(value: string): string {
  const compact = /^(\d{1,2})(\d{2})$/.exec(value);
  if (compact) return `${compact[1]?.padStart(2, '0') ?? ''}:${compact[2] ?? ''}`;
  return value;
}

/** Feed-added sub-attributes we know how to label; any other extra renders as `key: value`. */
const EXTRA_ENTRY_FORMATTERS: Record<string, (text: string, t: TFunction<'common'>) => string> = {
  handling_cutoff_time: (text, t) =>
    t('sourceShipping.extras.handlingCutoffTime', { time: formatCutoffTime(text) }),
};

/** Sub-keys of a shipping entry rendered by `formatShippingEntry` itself. */
const KNOWN_ENTRY_KEYS = new Set([
  'country',
  'region',
  'postal_code',
  'location_id',
  'location_group_name',
  'service',
  'price',
  'min_handling_time',
  'max_handling_time',
  'min_transit_time',
  'max_transit_time',
]);

/** Renders an unknown extra sub-attribute value without `[object Object]`. */
function stringifyExtra(value: unknown): string | undefined {
  if (value === null || value === undefined || value === '') return undefined;
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  return JSON.stringify(value);
}

/**
 * One shipping rule as a single line —
 * "US / CA · Standard · $12.00 · handling 1–3 business days · transit 2–5 business days".
 * Only the sub-fields the feed set appear; unknown extra sub-attributes are appended as `key: value`.
 */
export function formatShippingEntry(entry: ShippingEntry, t: TFunction<'common'>): string {
  const location = [entry.country, entry.region, entry.postal_code, entry.location_group_name]
    .filter((v): v is string => typeof v === 'string' && v.trim() !== '')
    .join(' / ');
  const handling = formatDayRange(entry.min_handling_time, entry.max_handling_time, t);
  const transit = formatDayRange(entry.min_transit_time, entry.max_transit_time, t);
  const parts: (string | undefined)[] = [
    location || t('sourceShipping.allLocations'),
    typeof entry.location_id === 'number'
      ? t('sourceShipping.locationId', { id: entry.location_id })
      : undefined,
    typeof entry.service === 'string' ? entry.service : undefined,
    formatShippingCost(entry.price, t),
    handling ? t('sourceShipping.handling', { range: handling }) : undefined,
    transit ? t('sourceShipping.transit', { range: transit }) : undefined,
  ];
  for (const [key, value] of Object.entries(entry)) {
    if (KNOWN_ENTRY_KEYS.has(key)) continue;
    const text = stringifyExtra(value);
    if (text === undefined) continue;
    const format = EXTRA_ENTRY_FORMATTERS[key];
    parts.push(format ? format(text, t) : `${key}: ${text}`);
  }
  return parts.filter((p): p is string => p !== undefined && p !== '').join(' · ');
}

/**
 * One threshold as "US · free over $50.00". A 0 threshold means every order ships
 * free; an entry with no usable price (the feed may send just `{country}`) says so
 * explicitly rather than rendering a bare country code.
 */
export function formatFreeShippingThreshold(
  threshold: FreeShippingThreshold,
  t: TFunction<'common'>,
): string {
  const price = formatShippingPrice(threshold.price_threshold);
  const rule =
    threshold.price_threshold?.value === 0
      ? t('sourceShipping.freeOnEveryOrder')
      : price
        ? t('sourceShipping.freeOver', { price })
        : t('sourceShipping.thresholdNotProvided');
  return [threshold.country ?? t('sourceShipping.allLocations'), rule].join(' · ');
}

/** Formats one already-parsed value by kind; `undefined` means "not provided". */
function formatByKind(
  kind: SourceShippingKind,
  value: unknown,
  t: TFunction<'common'>,
): string | undefined {
  if (value === null || value === undefined) return undefined;
  switch (kind) {
    case 'shipping':
      return (value as ShippingEntry[]).map((e) => formatShippingEntry(e, t)).join('; ');
    case 'threshold':
      return (value as FreeShippingThreshold[])
        .map((th) => formatFreeShippingThreshold(th, t))
        .join('; ');
    case 'measurement':
      return formatMeasurement(value as Measurement);
    case 'text':
      return value as string;
    case 'days':
      return t('sourceShipping.days', { count: value as number });
    case 'count':
      return decimalFormatter.format(value as number);
    case 'boolean':
      return value === true ? t('sourceShipping.yes') : t('sourceShipping.no');
  }
}

/** Display string for one attribute of a parsed set; `undefined` = not provided. */
export function formatSourceShippingField(
  attrs: SourceShippingAttributes,
  key: keyof SourceShippingAttributes,
  t: TFunction<'common'>,
): string | undefined {
  const field = SOURCE_SHIPPING_FIELDS.find((f) => f.key === key);
  return field ? formatByKind(field.kind, attrs[key], t) : undefined;
}

/**
 * Display string for a raw curation column value (version-history snapshots):
 * parses then formats by the column's kind. Returns `undefined` for a column
 * that isn't one of the 17, or whose value is unset/unusable.
 */
export function formatSourceShippingWireValue(
  wireKey: string,
  raw: unknown,
  t: TFunction<'common'>,
): string | undefined {
  const field = FIELD_BY_WIRE_KEY.get(wireKey);
  if (!field) return undefined;
  return formatByKind(field.kind, parseByKind(field.kind, raw), t);
}
